import {
  FormEvent,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import {
  approveClassification,
  approveFinal,
  attachmentUrl,
  fetchMarkdown,
  getRequirement,
  markdownDownloadUrl,
  rerefineRequirement,
  revokeApproval,
  revokeClassification,
  updateRequirement,
  type Requirement,
  type RequirementClassification,
  type UpdateRequirementInput,
} from '../api/requirements';
import { ClassBadge } from './ClassBadge';
import { ProgressRail } from './ProgressRail';
import { StatusPipeline } from './StatusPipeline';
import { SectionCard } from './SectionCard';
import { relTime } from '../lib/relTime';
import { titleOf, shortId } from '../lib/requirementText';
import { statusLabel } from '../lib/statusMapping';

type Tab = 'aufbereitung' | 'rohdaten' | 'markdown';

type FormState = {
  readonly classification: RequirementClassification;
  readonly customer: string;
  readonly project: string;
  readonly context: string;
  readonly summary: string;
};

type Props = {
  readonly requirementId: string;
  readonly onClose: () => void;
  readonly onMutated?: (message: string) => void;
};

function toForm(r: Requirement): FormState {
  return {
    classification: r.classification,
    customer: r.customer ?? '',
    project: r.project ?? '',
    context: r.context ?? '',
    summary: r.summary ?? '',
  };
}

function diff(original: Requirement, form: FormState): UpdateRequirementInput | null {
  const patch: UpdateRequirementInput = {};
  if (form.classification !== original.classification)
    patch.classification = form.classification;
  if ((form.customer || null) !== (original.customer ?? null))
    patch.customer = form.customer;
  if ((form.project || null) !== (original.project ?? null))
    patch.project = form.project;
  if ((form.context || null) !== (original.context ?? null))
    patch.context = form.context;
  if ((form.summary || null) !== (original.summary ?? null))
    patch.summary = form.summary;
  return Object.keys(patch).length > 0 ? patch : null;
}

export function DetailDrawer({ requirementId, onClose, onMutated }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('aufbereitung');
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    setTab('aufbereitung');
  }, [requirementId]);

  const query = useQuery({
    queryKey: ['requirements', requirementId],
    queryFn: () => getRequirement(requirementId),
    refetchInterval: (q) => {
      const r = q.state.data as Requirement | undefined;
      if (!r || r.lastError) return false;
      return r.status === 'INTAKE' || r.status === 'REFINING' ? 3000 : false;
    },
  });

  useEffect(() => {
    if (query.data) setForm(toForm(query.data));
  }, [query.data]);

  const requirement = query.data;

  const markdownQuery = useQuery({
    queryKey: ['requirements', requirementId, 'markdown'],
    queryFn: () => fetchMarkdown(requirementId),
    enabled: requirement?.status === 'RELEASED',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['requirements'] });
    queryClient.invalidateQueries({ queryKey: ['requirements', requirementId] });
    queryClient.invalidateQueries({ queryKey: ['requirements', requirementId, 'markdown'] });
  };

  const saveMutation = useMutation({
    mutationFn: (patch: UpdateRequirementInput) => updateRequirement(requirementId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Änderungen gespeichert');
    },
  });

  const approveClassMutation = useMutation({
    mutationFn: () => approveClassification(requirementId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Gate 1 freigegeben · Aufbereitung wird angestoßen');
    },
  });

  const approveFinalMutation = useMutation({
    mutationFn: () => approveFinal(requirementId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Final freigegeben · Markdown-Export bereit');
    },
  });

  const rerefineMutation = useMutation({
    mutationFn: () => rerefineRequirement(requirementId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Aufbereitung wird erneut angestoßen');
    },
  });

  const revokeClassMutation = useMutation({
    mutationFn: () => revokeClassification(requirementId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Klassifikation zurückgenommen');
    },
  });

  const revokeFinalMutation = useMutation({
    mutationFn: () => revokeApproval(requirementId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['requirements', requirementId], updated);
      invalidate();
      onMutated?.('Finale Freigabe zurückgenommen');
    },
  });

  const actionError =
    approveClassMutation.isError ||
    approveFinalMutation.isError ||
    rerefineMutation.isError ||
    revokeClassMutation.isError ||
    revokeFinalMutation.isError;

  const patch = useMemo(
    () => (requirement && form ? diff(requirement, form) : null),
    [requirement, form],
  );

  const startClose = () => {
    setOpen(false);
    setTimeout(onClose, 200);
  };

  return (
    <>
      <div
        className={'detail-backdrop ' + (open ? 'open' : '')}
        onClick={startClose}
        aria-hidden="true"
      />
      <aside
        className={'detail ' + (open ? 'open' : '')}
        role="dialog"
        aria-modal="true"
        aria-label="Anforderungs-Details"
      >
        {query.error ? (
          <DrawerError onClose={startClose} />
        ) : query.isLoading || !requirement || !form ? (
          <DrawerLoading onClose={startClose} />
        ) : (
          <DrawerContent
            requirement={requirement}
            form={form}
            setForm={setForm}
            tab={tab}
            setTab={setTab}
            patch={patch}
            saving={saveMutation.isPending}
            saveError={saveMutation.isError}
            onSave={(e) => {
              e.preventDefault();
              if (patch) saveMutation.mutate(patch);
            }}
            onResetForm={() => setForm(toForm(requirement))}
            onClose={startClose}
            onApproveClass={() => approveClassMutation.mutate()}
            onApproveFinal={() => approveFinalMutation.mutate()}
            onRerefine={() => rerefineMutation.mutate()}
            onRevokeClass={() => revokeClassMutation.mutate()}
            onRevokeFinal={() => revokeFinalMutation.mutate()}
            mutationPending={
              approveClassMutation.isPending ||
              approveFinalMutation.isPending ||
              rerefineMutation.isPending ||
              revokeClassMutation.isPending ||
              revokeFinalMutation.isPending
            }
            actionError={actionError}
            markdown={markdownQuery.data}
            markdownLoading={markdownQuery.isLoading}
          />
        )}
      </aside>
    </>
  );
}

function DrawerError({ onClose }: { readonly onClose: () => void }) {
  return (
    <>
      <div className="detail-head">
        <div className="h-main">
          <div className="h-id">Fehler</div>
          <h1>Anforderung nicht gefunden</h1>
        </div>
        <button type="button" className="detail-close" onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </button>
      </div>
      <div className="detail-body">
        <div className="detail-main">
          <div className="alert-banner alert-error" role="alert">
            Die Anforderung konnte nicht geladen werden.
          </div>
        </div>
      </div>
    </>
  );
}

function DrawerLoading({ onClose }: { readonly onClose: () => void }) {
  return (
    <>
      <div className="detail-head">
        <div className="h-main">
          <div className="h-id">Lade …</div>
          <h1>Anforderung wird geladen</h1>
        </div>
        <button type="button" className="detail-close" onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </button>
      </div>
      <div className="detail-body">
        <div className="detail-main">
          <div className="alert-banner alert-progress">
            <span className="spinner-dot" />
            Daten werden geladen.
          </div>
        </div>
      </div>
    </>
  );
}

type ContentProps = {
  readonly requirement: Requirement;
  readonly form: FormState;
  readonly setForm: (f: FormState) => void;
  readonly tab: Tab;
  readonly setTab: (t: Tab) => void;
  readonly patch: UpdateRequirementInput | null;
  readonly saving: boolean;
  readonly saveError: boolean;
  readonly onSave: (e: FormEvent) => void;
  readonly onResetForm: () => void;
  readonly onClose: () => void;
  readonly onApproveClass: () => void;
  readonly onApproveFinal: () => void;
  readonly onRerefine: () => void;
  readonly onRevokeClass: () => void;
  readonly onRevokeFinal: () => void;
  readonly mutationPending: boolean;
  readonly actionError: boolean;
  readonly markdown: string | undefined;
  readonly markdownLoading: boolean;
};

function DrawerContent(props: ContentProps) {
  const {
    requirement,
    form,
    setForm,
    tab,
    setTab,
    patch,
    saving,
    saveError,
    onSave,
    onResetForm,
    onClose,
    onApproveClass,
    onApproveFinal,
    onRerefine,
    onRevokeClass,
    onRevokeFinal,
    mutationPending,
    actionError,
    markdown,
    markdownLoading,
  } = props;

  const missing = requirement.missingInfo ?? [];
  const hasError = !!requirement.lastError;
  const isRefining = requirement.status === 'REFINING' && !hasError;
  const isEditable =
    requirement.status !== 'RELEASED' && requirement.status !== 'ARCHIVED';
  const showGate1 = requirement.status === 'TRIAGE';
  const showGate2 = requirement.status === 'READY_FOR_RELEASE' && missing.length === 0;
  const canRevokeClass =
    requirement.status === 'REFINING' ||
    requirement.status === 'READY_FOR_RELEASE' ||
    requirement.status === 'CLARIFICATION';
  const canRerefine =
    requirement.status === 'CLARIFICATION' ||
    requirement.status === 'READY_FOR_RELEASE';
  const canRevokeFinal = requirement.status === 'RELEASED';
  const tabs: readonly { id: Tab; label: string; count?: number }[] = [
    { id: 'aufbereitung', label: 'Aufbereitung' },
    { id: 'rohdaten', label: 'Rohdaten', count: requirement.attachments.length || undefined },
    { id: 'markdown', label: 'Markdown-Export' },
  ];

  const customerName = requirement.customer?.trim() || '—';
  const project = requirement.project?.trim() || '—';
  const rawCreator = requirement.rawInput?.creatorId;
  const creator = typeof rawCreator === 'string' && rawCreator.length > 0 ? rawCreator : '—';

  return (
    <>
      <div className="detail-head">
        <div className="h-main">
          <div className="h-id">
            <span>{shortId(requirement.id)}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <ClassBadge classification={requirement.classification} />
            <span style={{ opacity: 0.4 }}>·</span>
            <StatusPipeline status={requirement.status} />
          </div>
          <h1>{titleOf(requirement)}</h1>
          <div className="h-meta">
            <span>
              <b>Kunde:</b> {customerName}
            </span>
            <span>
              <b>Projekt:</b> {project}
            </span>
            <span>
              <b>Ersteller:</b> {creator}
            </span>
            <span>
              <b>Eingang:</b> {relTime(requirement.createdAt)}
            </span>
          </div>
        </div>
        <button type="button" className="detail-close" onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </button>
      </div>

      <div className="detail-body">
        <div className="detail-grid">
          <div className="detail-main">
            <ProgressRail status={requirement.status} />

            <div className="detail-tabs" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={'dt-tab ' + (tab === t.id ? 'active' : '')}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                  {t.count !== undefined && <span className="mini-c">{t.count}</span>}
                </button>
              ))}
            </div>

            {hasError && (
              <div className="alert-banner alert-error" role="alert">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <strong>Letzter Job fehlgeschlagen</strong>
                  {requirement.lastErrorAt && (
                    <span style={{ fontSize: 'var(--fz-xs)', color: 'var(--fg-muted)' }}>
                      {new Date(requirement.lastErrorAt).toLocaleString('de-DE')}
                    </span>
                  )}
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 0.5rem' }}>
                  {requirement.lastError}
                </pre>
                <div style={{ fontSize: 'var(--fz-sm)' }}>
                  Status zurückgesetzt auf <code>{requirement.status}</code>. Nach Behebung der Ursache (z.&nbsp;B. API-Credits) kann die Klassifikation erneut freigegeben oder zurückgenommen werden.
                </div>
              </div>
            )}

            {isRefining && (
              <div className="alert-banner alert-progress">
                <span className="spinner-dot" />
                Aufbereitung läuft. Die Seite aktualisiert sich automatisch.
              </div>
            )}

            {tab === 'aufbereitung' && (
              <AufbereitungTab
                requirement={requirement}
                form={form}
                setForm={setForm}
                isEditable={isEditable}
                saving={saving}
                saveError={saveError}
                patch={patch}
                onSave={onSave}
                onResetForm={onResetForm}
                missing={missing}
              />
            )}

            {tab === 'rohdaten' && <RohdatenTab requirement={requirement} />}

            {tab === 'markdown' && (
              <MarkdownTab
                requirement={requirement}
                markdown={markdown}
                markdownLoading={markdownLoading}
              />
            )}

            {actionError && (
              <div className="alert-banner alert-error" role="alert">
                Aktion fehlgeschlagen. Bitte Status neu laden.
              </div>
            )}
          </div>

          <aside className="detail-side">
            <div className="panel">
              <h4>Metadaten</h4>
              <KV k="ID" v={shortId(requirement.id)} />
              <KV k="Klassifikation" v={<ClassBadge classification={requirement.classification} />} />
              <KV k="Kunde" v={customerName} />
              <KV k="Projekt" v={project} />
              <KV k="Status" v={statusLabel(requirement.status)} />
              <KV k="Eingang" v={new Date(requirement.createdAt).toLocaleString('de-DE')} />
              {requirement.markdownExportedAt && (
                <KV
                  k="Exportiert"
                  v={new Date(requirement.markdownExportedAt).toLocaleString('de-DE')}
                />
              )}
            </div>
          </aside>
        </div>
      </div>

      <div className="detail-foot">
        <div className="foot-left">
          {canRerefine && (
            <button
              type="button"
              className="btn-ghost-app"
              disabled={mutationPending || !!patch}
              title={patch ? 'Erst Änderungen speichern oder verwerfen.' : undefined}
              onClick={() => {
                if (
                  confirm(
                    'Aufbereitung erneut anstoßen? Zusammenfassung, Struktur und offene Punkte werden durch das neue KI-Ergebnis ersetzt.',
                  )
                ) {
                  onRerefine();
                }
              }}
            >
              Aufbereitung erneut anstoßen
            </button>
          )}
          {canRevokeClass && (
            <button
              type="button"
              className="btn-ghost-app"
              disabled={mutationPending}
              onClick={() => {
                if (
                  confirm(
                    'Klassifikation zurücknehmen? Zusammenfassung und Struktur werden verworfen.',
                  )
                ) {
                  onRevokeClass();
                }
              }}
            >
              Klassifikation zurücknehmen
            </button>
          )}
          {canRevokeFinal && (
            <button
              type="button"
              className="btn-danger-app"
              disabled={mutationPending}
              onClick={() => {
                if (confirm('Finale Freigabe zurücknehmen? Export wird gelöscht.')) {
                  onRevokeFinal();
                }
              }}
            >
              Finale Freigabe zurücknehmen
            </button>
          )}
        </div>
        <div className="foot-right">
          {showGate1 && (
            <button
              type="button"
              className={'btn-lime ' + (mutationPending || patch ? 'disabled' : '')}
              disabled={mutationPending || !!patch}
              title={patch ? 'Erst Änderungen speichern oder verwerfen.' : undefined}
              onClick={onApproveClass}
            >
              Klassifikation freigeben · Gate 1
            </button>
          )}
          {showGate2 && (
            <button
              type="button"
              className={'btn-lime ' + (mutationPending || patch ? 'disabled' : '')}
              disabled={mutationPending || !!patch}
              title={patch ? 'Erst Änderungen speichern oder verwerfen.' : undefined}
              onClick={onApproveFinal}
            >
              Final freigeben · Gate 2
            </button>
          )}
          {requirement.status === 'READY_FOR_RELEASE' && missing.length > 0 && (
            <span className="foot-hint">Offene Rückfragen blockieren die Freigabe.</span>
          )}
          {requirement.status === 'INTAKE' && (
            <span className="foot-hint">KI klassifiziert · Aktionen folgen gleich.</span>
          )}
          {requirement.status === 'REFINING' && (
            <span className="foot-hint">Refine-Agent läuft · bitte warten.</span>
          )}
          {requirement.status === 'RELEASED' && (
            <a
              className="btn-lime"
              href={markdownDownloadUrl(requirement.id)}
              download
            >
              Markdown herunterladen
            </a>
          )}
        </div>
      </div>
    </>
  );
}

type AufbereitungTabProps = {
  readonly requirement: Requirement;
  readonly form: FormState;
  readonly setForm: (f: FormState) => void;
  readonly isEditable: boolean;
  readonly saving: boolean;
  readonly saveError: boolean;
  readonly patch: UpdateRequirementInput | null;
  readonly onSave: (e: FormEvent) => void;
  readonly onResetForm: () => void;
  readonly missing: readonly string[];
};

function AufbereitungTab(props: AufbereitungTabProps) {
  const {
    requirement,
    form,
    setForm,
    isEditable,
    saving,
    saveError,
    patch,
    onSave,
    onResetForm,
    missing,
  } = props;

  return (
    <>
      <SectionCard title="Aufbereitung">
        <form onSubmit={onSave} noValidate>
          <div className="form-grid">
            <div className="field">
              <label className="field-label" htmlFor="drawer-classification">
                Klassifikation
              </label>
              <select
                id="drawer-classification"
                className="select"
                value={form.classification}
                disabled={!isEditable || saving}
                onChange={(e) =>
                  setForm({
                    ...form,
                    classification: e.target.value as RequirementClassification,
                  })
                }
              >
                <option value="UNKNOWN">Unklar</option>
                <option value="BUG">Bug</option>
                <option value="USER_STORY">User Story</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label" htmlFor="drawer-customer">
                Kunde
              </label>
              <input
                id="drawer-customer"
                className="input"
                type="text"
                value={form.customer}
                disabled={!isEditable || saving}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="drawer-project">
                Projekt
              </label>
              <input
                id="drawer-project"
                className="input"
                type="text"
                value={form.project}
                disabled={!isEditable || saving}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
              />
            </div>
            <div className="field field-full">
              <label className="field-label" htmlFor="drawer-context">
                Kontext
              </label>
              <textarea
                id="drawer-context"
                className="textarea"
                rows={3}
                value={form.context}
                disabled={!isEditable || saving}
                maxLength={5000}
                placeholder="Hintergrund für die KI-Aufbereitung (Softwaretyp, Tech-Stack, bestehendes/neues Projekt …). Nach einer Änderung die Aufbereitung erneut anstoßen, um Rückfragen neu zu bewerten."
                onChange={(e) => setForm({ ...form, context: e.target.value })}
              />
            </div>
            <div className="field field-full">
              <label className="field-label" htmlFor="drawer-summary">
                Zusammenfassung
              </label>
              <textarea
                id="drawer-summary"
                className="textarea"
                rows={4}
                value={form.summary}
                disabled={!isEditable || saving}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
              />
            </div>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn-ghost-app"
              disabled={!patch || saving}
              onClick={onResetForm}
            >
              Zurücksetzen
            </button>
            <button
              type="submit"
              className="btn-primary-app"
              disabled={!patch || saving || !isEditable}
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
          {saveError && (
            <div className="alert-banner alert-error" style={{ marginTop: '0.75rem' }}>
              Fehler beim Speichern.
            </div>
          )}
        </form>
      </SectionCard>

      <StructuredOutputCard requirement={requirement} />

      {missing.length > 0 && (
        <SectionCard
          title="Offene Punkte"
          meta={`· ${missing.length} vom Refine-Agenten erkannt`}
        >
          <div className="missing-card">
            <div className="mi-head">
              Die Anforderung ist ohne diese Antworten nicht abschließend bewertbar.
            </div>
            <ul>
              {missing.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        </SectionCard>
      )}
    </>
  );
}

function StructuredOutputCard({ requirement }: { readonly requirement: Requirement }) {
  const out = requirement.structuredOutput;
  if (!out || (Array.isArray(out) && out.length === 0)) return null;
  if (typeof out !== 'object') return null;

  const entries = Object.entries(out as Record<string, unknown>);
  const labelFor = (k: string) =>
    requirement.classification === 'BUG'
      ? bugLabels[k] ?? k
      : storyLabels[k] ?? k;

  return (
    <SectionCard title="Strukturiertes Zielformat" meta="· KI">
      {entries.map(([k, v]) => (
        <div className="story-field" key={k}>
          <div className="k">{labelFor(k)}</div>
          <div className="v">{renderValue(v)}</div>
        </div>
      ))}
    </SectionCard>
  );
}

const storyLabels: Record<string, string> = {
  as: 'Als',
  role: 'Als',
  want: 'möchte ich',
  goal: 'möchte ich',
  so: 'damit',
  reason: 'damit',
  ac: 'Akzeptanz­kriterien',
  acceptance: 'Akzeptanz­kriterien',
  acceptanceCriteria: 'Akzeptanz­kriterien',
};

const bugLabels: Record<string, string> = {
  steps: 'Schritte',
  expected: 'Erwartet',
  actual: 'Tatsächlich',
  env: 'Umgebung',
  environment: 'Umgebung',
};

function renderValue(v: unknown): ReactNode {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) {
    return (
      <ul className="ac-list">
        {v.map((item, i) => (
          <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    );
  }
  if (typeof v === 'object') {
    return <pre className="json-block">{JSON.stringify(v, null, 2)}</pre>;
  }
  return String(v);
}

function RohdatenTab({ requirement }: { readonly requirement: Requirement }) {
  const rawTitle = requirement.rawInput?.title?.trim();
  const rawDescription = requirement.rawInput?.description?.trim();
  return (
    <SectionCard title="Original-Eingabe" meta={`· ${relTime(requirement.createdAt)}`}>
      {rawTitle && <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{rawTitle}</div>}
      <div className="raw-block">{rawDescription || '—'}</div>
      {requirement.attachments.length > 0 && (
        <div className="attachments">
          {requirement.attachments.map((a) => (
            <a
              key={a.id}
              className="att"
              href={attachmentUrl(requirement.id, a.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {a.filename}
              <span className="sz">
                · {Math.round(a.sizeBytes / 1024)} kB
              </span>
            </a>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

type MarkdownTabProps = {
  readonly requirement: Requirement;
  readonly markdown: string | undefined;
  readonly markdownLoading: boolean;
};

function MarkdownTab({ requirement, markdown, markdownLoading }: MarkdownTabProps) {
  if (requirement.status !== 'RELEASED') {
    return (
      <SectionCard title="Markdown-Export">
        <p style={{ color: 'var(--fg-muted)', margin: 0 }}>
          Markdown wird nach finaler Freigabe verfügbar.
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Markdown-Export">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
        <a
          className="btn-ghost-app"
          href={markdownDownloadUrl(requirement.id)}
          download
        >
          Download .md
        </a>
      </div>
      {markdownLoading && (
        <div className="alert-banner alert-progress">
          <span className="spinner-dot" />
          Lade Markdown …
        </div>
      )}
      {markdown && (
        <div className="md-preview">
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </div>
      )}
    </SectionCard>
  );
}

function KV({ k, v }: { readonly k: string; readonly v: ReactNode }) {
  return (
    <div className="row-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
